import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import SystemHeader from './SystemHeader';
import { getAllFeatureConfigs } from '../config/featureDisplayConfig';
import { DashboardAPI } from '../services/feature-api';
import { useFeatureConfig } from '../contexts/FeatureConfigContext';

// Màn hình chính — 25 tính năng gom 6 nhóm theo sơ đồ "Giải thích APP Hệ sinh thái toàn diện" (anh Bình, 23/09/2026).
// Nhóm / nhóm con của từng tính năng nằm ở featureDisplayConfig.json (nhom, nhomCon); ở đây chỉ giữ tên + màu nhóm.
// Kiểu "Bản thử A — Ngọc lục bảo": mỗi nhóm 1 thẻ trắng, ô icon gradient theo màu nhóm, mục "Đang triển khai" viền đứt + mờ.
const NHOM = {
    1: { ten: 'Quản trị bản thân', mau: '#0F766E' },
    2: { ten: 'Quản trị đội nhóm', mau: '#1D4ED8' },
    3: { ten: 'Kinh doanh', mau: '#B45309' },
    4: { ten: 'Thi đua', mau: '#BE185D', con: { Online: 'wifi', Offline: 'handshake' } },
    5: { ten: 'Học tập, rèn luyện', mau: '#6D28D9' },
    6: { ten: 'Thành quả', mau: '#C2410C', con: { 'Cá nhân': 'person_celebrate', 'Công ty': 'apartment' } },
};

// '#B45309' + 0.28 → màu sáng hơn cho đầu gradient
const sang = (hex, p) => {
    const n = parseInt(hex.slice(1), 16);
    const f = (c) => Math.round(c + (255 - c) * p);
    return `rgb(${f(n >> 16)},${f((n >> 8) & 255)},${f(n & 255)})`;
};

const Dashboard = ({ userInfo, onLogout, totalPoints, accessToken, onRefreshPoints }) => {
    const navigate = useNavigate();
    const [stats, setStats] = useState({});
    const [showThiDua, setShowThiDua] = useState(false);
    const { features } = useFeatureConfig();
    const biKhoa = (key) => features.some((f) => f.featureKey === key && f.stt != null && f.isAccess === false);

    const loadStats = useCallback(async () => {
        if (!accessToken) return;
        const now = new Date();
        try {
            const result = await DashboardAPI.getStats(accessToken, now.getMonth() + 1, now.getFullYear());
            // Backend returns { reels: { total, approved }, market: { total }, ... } — key trùng key featureDisplayConfig
            if (result.success && result.data) setStats(result.data);
        } catch (e) { console.warn('Failed to load dashboard stats:', e); }
    }, [accessToken]);
    useEffect(() => { loadStats(); }, [loadStats]);

    const items = getAllFeatureConfigs().filter((f) => f.enabled !== false).sort((a, b) => (a.order || 99) - (b.order || 99));

    const getStatText = (item) => {
        if (!item.dashboardStat?.enabled) return null;
        const val = stats[item.key]?.[item.dashboardStat.statKey || 'total'];
        if (val === undefined || val === null) return null;
        return `${Number(val).toLocaleString('vi-VN')} ${item.dashboardStat.label || ''}`.trim();
    };

    const O = ({ item, mau }) => {
        const dtk = !!item.dangTrienKhai;
        const statText = getStatText(item);
        return (
            <div className="flex flex-col items-center gap-1.5 cursor-pointer group" onClick={() => navigate(item.path)} style={{ WebkitTapHighlightColor: 'transparent' }}>
                <div className="relative">
                    <div className={`w-[60px] h-[60px] rounded-[19px] flex items-center justify-center relative overflow-hidden transition-transform duration-150 group-active:scale-90 ${dtk ? 'bg-white text-gray-400 border-[1.5px] border-dashed border-gray-300' : 'text-white'}`}
                         style={dtk ? {} : { background: `linear-gradient(145deg, ${sang(mau, 0.28)}, ${mau})`, boxShadow: `0 8px 18px -8px ${mau}, inset 0 1px 0 rgba(255,255,255,.35)` }}>
                        {!dtk && <span className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(180deg, rgba(255,255,255,.22), transparent 55%)' }} />}
                        <span className="material-symbols-rounded relative" style={{ fontSize: 30, filter: dtk ? 'none' : 'drop-shadow(0 2px 2px rgba(0,0,0,.18))' }}>{item.icon}</span>
                    </div>
                    <span className="absolute -top-[7px] -left-[7px] min-w-[21px] h-[21px] px-1 rounded-full bg-white text-[10.5px] font-extrabold flex items-center justify-center shadow-md border-[1.5px]"
                          style={{ color: dtk ? '#9CA3AF' : mau, borderColor: dtk ? '#CBD5E1' : mau }}>
                        {item.order}
                    </span>
                    {biKhoa(item.key) && (
                        <span className="absolute -bottom-1 -right-1 w-[22px] h-[22px] rounded-full bg-red-600 text-white flex items-center justify-center shadow-md border-2 border-white">
                            <span className="material-symbols-rounded" style={{ fontSize: 13 }}>lock</span>
                        </span>
                    )}
                </div>
                <span className={`text-[11px] font-semibold text-center leading-tight max-w-[82px] ${dtk ? 'text-gray-400' : 'text-gray-800'}`}>{item.name}</span>
                {dtk ? (
                    <span className="text-[9px] font-bold text-amber-700 bg-amber-100 rounded-full px-1.5 py-0.5 -mt-0.5">Đang triển khai</span>
                ) : statText && (
                    <span className="text-[9.5px] font-bold rounded-full px-2 py-0.5 -mt-0.5" style={{ backgroundColor: `${mau}1F`, color: mau }}>{statText}</span>
                )}
            </div>
        );
    };

    const Luoi = ({ list, mau }) => (
        <div className="grid grid-cols-4 gap-y-3 gap-x-1.5">{list.map((it) => <O key={it.key} item={it} mau={mau} />)}</div>
    );

    const userName = userInfo?.name || 'User';

    return (
        <div className="min-h-screen bg-[#F4F6F3] font-['Inter'] text-gray-900 pb-8">
            <SystemHeader userInfo={userInfo} onLogout={onLogout} totalPoints={totalPoints} accessToken={accessToken} onRefreshPoints={onRefreshPoints} />

            {/* Header: gradient xanh ngọc + quầng vàng */}
            <div className="px-4 py-3 flex flex-wrap items-center gap-3 relative overflow-hidden"
                 style={{ background: 'radial-gradient(120% 140% at 0% 0%, #1E5A35 0%, #0F3D2A 55%, #0A2B1D 100%)', borderRadius: '0 0 1.6rem 1.6rem' }}>
                <div className="absolute -top-24 -right-10 w-64 h-64 rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(231,196,106,.35), transparent 60%)', filter: 'blur(10px)' }} />
                <div className="relative flex-shrink-0">
                    <img alt="" className="w-11 h-11 rounded-full border-2 border-white/30 object-cover" src={userInfo?.avatar || 'https://ui-avatars.com/api/?name=' + encodeURIComponent(userName)} />
                    <div className="absolute -bottom-0.5 -right-0.5 bg-green-400 w-3.5 h-3.5 rounded-full border-2 border-white" />
                </div>
                <div className="flex-1 min-w-0 relative">
                    <p className="text-white/60 text-[10px] font-medium uppercase tracking-[.14em]">Xin chào</p>
                    <h1 className="text-white text-base font-bold truncate">{userName}</h1>
                </div>
                <div className="text-right flex-shrink-0 cursor-pointer active:scale-95 transition-transform relative" onClick={() => navigate('/diem-so')}>
                    <p className="text-white/60 text-[10px]">Tổng điểm</p>
                    <p className="text-[#E7C46A] text-[22px] font-extrabold leading-tight tracking-tight">{totalPoints || 0}</p>
                </div>
                {/* Khẩu hiệu anh Bình chốt 02/10/2026 */}
                <p className="basis-full relative text-center text-[17px] font-black leading-snug rounded-xl py-2 px-3 bg-white/10 border border-[#E7C46A]/40">
                    <span className="text-white">Vào đây để: </span><span className="text-[#E7C46A] uppercase tracking-wide">Quản Trị Sự Nghiệp</span>
                </p>
            </div>

            <main className="px-4 mt-3.5 space-y-3.5">
                {Object.entries(NHOM).map(([stt, g], gi) => {
                    const cua = items.filter((f) => String(f.nhom) === stt);
                    if (!cua.length) return null;
                    return (
                        <section key={stt} className="bg-white rounded-[22px] px-3 pt-3.5 pb-3 border border-[#ECEFEC] len-vao"
                                 style={{ boxShadow: '0 1px 2px rgba(0,0,0,.04), 0 10px 24px -18px rgba(0,0,0,.25)', animationDelay: `${gi * 50}ms` }}>
                            <div className="flex items-center gap-2.5 mx-1 mb-3">
                                <span className="w-[30px] h-[30px] rounded-[10px] flex items-center justify-center text-white font-extrabold text-[13px] flex-shrink-0" style={{ backgroundColor: g.mau }}>{stt}</span>
                                <h2 className="text-[14.5px] font-bold flex-1 tracking-tight">{g.ten}</h2>
                                <span className="text-[11px] text-gray-500 font-semibold">{cua.length} mục</span>
                            </div>
                            {g.con ? Object.entries(g.con).map(([tenCon, icon]) => (
                                <div key={tenCon}>
                                    <div className="flex items-center gap-2 mx-1 mt-1.5 mb-2" style={{ color: g.mau }}>
                                        <span className="material-symbols-rounded" style={{ fontSize: 15 }}>{icon}</span>
                                        <b className="text-[11px] font-bold tracking-[.08em] uppercase">{tenCon}</b>
                                        <i className="flex-1 h-px" style={{ background: 'linear-gradient(90deg,#E5E7EB,transparent)' }} />
                                    </div>
                                    <Luoi list={cua.filter((f) => f.nhomCon === tenCon)} mau={g.mau} />
                                </div>
                            )) : <Luoi list={cua} mau={g.mau} />}
                        </section>
                    );
                })}

                {/* Thi đua image — clickable to expand */}
                <div className="rounded-2xl overflow-hidden shadow-sm border border-gray-100 cursor-pointer active:scale-[.98] transition-transform" onClick={() => setShowThiDua(true)}>
                    <img src="/images/thi-dua.jpg" alt="Bảng thi đua" className="w-full h-auto" onError={(e) => { e.target.parentElement.style.display = 'none'; }} />
                </div>
                <p className="text-center text-[10px] text-gray-300 pb-1">v57</p>
            </main>

            {showThiDua && (
                <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setShowThiDua(false)}>
                    <div className="relative max-w-2xl w-full max-h-[90vh]">
                        <button onClick={() => setShowThiDua(false)} className="absolute -top-3 -right-3 w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-lg text-gray-600 font-bold z-10">✕</button>
                        <img src="/images/thi-dua.jpg" alt="Bảng thi đua" className="w-full h-auto rounded-xl" />
                    </div>
                </div>
            )}
        </div>
    );
};

export default Dashboard;
